/**
 * 通用模糊查询接口
 * @author chaseZheng
 * @version 1.0
 * @since 2025-06-30
 */

import { request } from '../utils/http'
import type { AxiosRequestConfig } from 'axios'

// 导入数据类型枚举
export { UniversalSearchDataType } from '../enums/universal-search'

type RequestConfigWithErrorMessage = AxiosRequestConfig & { showErrorMessage?: boolean }

/**
 * 通用模糊查询请求参数
 */
export interface UniversalSearchRequest {
  /** 数据类型 */
  dataType: string
  /** 搜索关键词 */
  keyword?: string
  /** 主键ID列表，传入时进行精准查询 */
  ids?: string[]
  /** 页码，从1开始 */
  pageNum?: number
  /** 每页条数，最小1条，最大50条 */
  pageSize?: number
  /** 是否返回完整VO对象 */
  returnFullVO?: boolean
  /** 启用状态：1-启用，0-禁用，null-全部 */
  enabled?: number
  /** 是否查询平台数据（当前租户ID=1时查询平台数据，否则查询租户关联的平台数据） */
  isPlatformData?: boolean
  /** 是否按港口代码、英文名称、中文名称的命中优先级排序，默认false */
  portSearchMatchPriority?: boolean
  /** 是否查询全部仓库类型；仅对海外仓库有效 */
  allWarehouseTypes?: boolean
  /** 上级ID，用于层级查询（仅对行政区划有效） */
  parentId?: string | number
  /** 邮政编码，用于邮编相关查询 */
  postalCode?: string
  /** 额外参数 */
  [key: string]: any
}

/**
 * 通用模糊查询响应项
 */
export interface UniversalSearchItem {
  /** id */
  id: string
  /** 业务代码 */
  code: string
  /** 中文名称 */
  nameCn: string
  /** 英文名称 */
  nameEn: string
  /** 启用状态 */
  enabled: number
  /** 完整内容对象（新版本统一字段） */
  content?: Record<string, unknown>
}

/**
 * 通用模糊查询响应
 */
export type UniversalSearchResponse = UniversalSearchItem[]

/** 批量ID查询请求；不传 enabled 时用于历史数据反显。 */
export interface UniversalBatchSearchRequest {
  dataType: string
  ids: string[]
  returnFullVO?: boolean
  isPlatformData?: boolean
  enabled?: 0 | 1
}

export type UniversalBatchSearchResponse = Record<string, UniversalSearchItem[]>

/**
 * 批量关键词搜索请求参数
 */
export interface UniversalKeywordsSearchRequest {
  /** 数据类型 */
  dataType: string
  /** 关键词列表 */
  keywords: string[]
  /** 是否按港口代码、英文名称、中文名称的命中优先级排序，默认false */
  portSearchMatchPriority?: boolean
  /** 租户ID */
  tenantId?: number
  /** 是否查询平台数据 */
  isPlatformData?: boolean
  /** 启用状态 */
  enabled?: number
  /** 批次大小 */
  batchSize?: number
  /** 是否返回完整VO对象 */
  returnFullVO?: boolean
}

/**
 * 批量关键词搜索响应
 * Map<关键词, List<UniversalSearchVO>>
 */
export type UniversalKeywordsSearchResponse = Record<string, UniversalSearchItem[]>

/**
 * 支持的数据类型映射（基于后端策略模式）
 */
export const UNIVERSAL_SEARCH_DATA_TYPES = {
  // 基础数据类型
  COUNTRY: 'country',                           // 国家数据
  ADMINISTRATIVE_DIVISION: 'administrative_division', // 中国行政区划
  INTERNATIONAL_DIVISION: 'international_division',   // 国际行政区划
  POSTAL_CODE: 'postal_code',                   // 邮编数据

  // 运输相关
  SEAPORT: 'seaport',                          // 海港数据
  AIRPORT: 'airport',                          // 机场数据
  TERMINAL: 'terminal',                        // 码头数据
  CARRIER: 'carrier',                          // 承运人数据
  RAILWAY_STATION: 'railway_station',           // 铁路站点数据
  SHIPPING_AGENT: 'shipping_agent',            // 船舶代理
  ROUTE: 'route',                              // 航线数据

  // 仓储相关
  OVERSEAS_WAREHOUSE: 'overseas_warehouse',     // 海外仓库

  // 费用相关
  FEE_MANAGEMENT: 'fee_management',            // 费用管理
  CURRENCY: 'currency',                        // 币种数据
  EXCHANGE_RATE: 'exchange_rate',              // 汇率设置
  BILLING_UNIT: 'billing_unit',                // 计费单位
  DENSITY_DEGREE: 'density_degree'             // 密度等级
} as const

/**
 * 数据类型配置接口
 */
export interface UniversalSearchConfig {
  /** 数据类型标识 */
  dataType: string
  /** 是否支持层级查询 */
  supportsHierarchy?: boolean
  /** 是否支持parentId参数 */
  supportsParentId?: boolean
  /** 是否支持邮编查询 */
  supportsPostalCode?: boolean
  /** 默认启用状态 */
  defaultEnabled?: number
  /** 特殊参数配置 */
  extraParams?: Record<string, any>
  /** 描述信息 */
  description?: string
}

/**
 * 各数据类型的配置映射（基于后端策略实现）
 */
export const UNIVERSAL_SEARCH_CONFIGS: Record<string, UniversalSearchConfig> = {
  [UNIVERSAL_SEARCH_DATA_TYPES.COUNTRY]: {
    dataType: 'country',
    supportsHierarchy: false,
    supportsParentId: false,
    defaultEnabled: 1,
    description: '国家数据查询，支持租户和平台数据区分'
  },

  [UNIVERSAL_SEARCH_DATA_TYPES.ADMINISTRATIVE_DIVISION]: {
    dataType: 'administrative_division',
    supportsHierarchy: true,
    supportsParentId: true,
    supportsPostalCode: true,
    defaultEnabled: 1,
    description: '中国行政区划查询，支持层级查询和邮编查询'
  },

  [UNIVERSAL_SEARCH_DATA_TYPES.INTERNATIONAL_DIVISION]: {
    dataType: 'international_division',
    supportsHierarchy: true,
    supportsParentId: true,
    defaultEnabled: 1,
    description: '国际行政区划查询，支持层级查询'
  },

  [UNIVERSAL_SEARCH_DATA_TYPES.POSTAL_CODE]: {
    dataType: 'postal_code',
    supportsPostalCode: true,
    defaultEnabled: 1,
    description: '邮编数据查询，支持中美邮编'
  },

  [UNIVERSAL_SEARCH_DATA_TYPES.SEAPORT]: {
    dataType: 'seaport',
    defaultEnabled: 1,
    description: '海港数据查询'
  },

  [UNIVERSAL_SEARCH_DATA_TYPES.AIRPORT]: {
    dataType: 'airport',
    defaultEnabled: 1,
    description: '机场数据查询'
  },

  [UNIVERSAL_SEARCH_DATA_TYPES.TERMINAL]: {
    dataType: 'terminal',
    defaultEnabled: 1,
    description: '码头数据查询'
  },

  [UNIVERSAL_SEARCH_DATA_TYPES.CARRIER]: {
    dataType: 'carrier',
    defaultEnabled: 1,
    description: '承运人数据查询'
  },

  [UNIVERSAL_SEARCH_DATA_TYPES.RAILWAY_STATION]: {
    dataType: 'railway_station',
    defaultEnabled: 1,
    description: '铁路站点数据查询'
  },

  [UNIVERSAL_SEARCH_DATA_TYPES.SHIPPING_AGENT]: {
    dataType: 'shipping_agent',
    defaultEnabled: 1,
    description: '船舶代理数据查询'
  },

  [UNIVERSAL_SEARCH_DATA_TYPES.ROUTE]: {
    dataType: 'route',
    defaultEnabled: 1,
    description: '航线数据查询'
  },

  [UNIVERSAL_SEARCH_DATA_TYPES.OVERSEAS_WAREHOUSE]: {
    dataType: 'overseas_warehouse',
    defaultEnabled: 1,
    description: '海外仓库数据查询'
  },

  [UNIVERSAL_SEARCH_DATA_TYPES.FEE_MANAGEMENT]: {
    dataType: 'fee_management',
    defaultEnabled: 1,
    description: '费用管理数据查询'
  },

  [UNIVERSAL_SEARCH_DATA_TYPES.CURRENCY]: {
    dataType: 'currency',
    defaultEnabled: 1,
    description: '币种数据查询'
  },

  [UNIVERSAL_SEARCH_DATA_TYPES.EXCHANGE_RATE]: {
    dataType: 'exchange_rate',
    defaultEnabled: 1,
    description: '汇率设置数据查询'
  },

  [UNIVERSAL_SEARCH_DATA_TYPES.BILLING_UNIT]: {
    dataType: 'billing_unit',
    defaultEnabled: 1,
    description: '计费单位数据查询'
  },

  [UNIVERSAL_SEARCH_DATA_TYPES.DENSITY_DEGREE]: {
    dataType: 'density_degree',
    defaultEnabled: 1,
    description: '密度等级数据查询'
  }
}

/**
 * 通用模糊查询
 */
export const universalSearch = (
  params: UniversalSearchRequest,
  options?: { showErrorMessage?: boolean }
) => {
  // 获取配置信息
  const dataTypeConfig = UNIVERSAL_SEARCH_CONFIGS[params.dataType]

  // 构建最终参数
  const finalParams: UniversalSearchRequest = {
    ...params,
    // 设置默认启用状态
    enabled: params.enabled !== undefined ? params.enabled : dataTypeConfig?.defaultEnabled,
    // 添加配置中的额外参数
    ...dataTypeConfig?.extraParams
  }

  console.log('🔍 [API调用] universalSearch 参数:', finalParams)
  console.log('📋 [配置信息] 数据类型配置:', dataTypeConfig)

  return request.post<UniversalSearchResponse>(
    'mdm/universal-search/search',
    finalParams,
    { showErrorMessage: options?.showErrorMessage ?? false } as RequestConfigWithErrorMessage
  )
}

/**
 * 按ID批量查询基础数据；默认不按状态过滤，供历史值回显使用。
 */
export const universalSearchBatch = (requests: UniversalBatchSearchRequest[]) => {
  return request.post<UniversalBatchSearchResponse>('mdm/universal-search/search-batch', requests)
}

/**
 * 获取数据类型配置
 */
export const getUniversalSearchConfig = (dataType: string): UniversalSearchConfig | undefined => {
  return UNIVERSAL_SEARCH_CONFIGS[dataType]
}

/**
 * 检查数据类型是否支持特定功能
 */
export const checkDataTypeSupport = (dataType: string, feature: keyof UniversalSearchConfig): boolean => {
  const config = getUniversalSearchConfig(dataType)
  return Boolean(config?.[feature])
}

/**
 * 构建层级查询参数
 */
export const buildHierarchyParams = (dataType: string, parentId?: string | number): Partial<UniversalSearchRequest> => {
  const config = getUniversalSearchConfig(dataType)

  if (!config?.supportsParentId || !parentId) {
    return {}
  }

  return {
    parentId,
    isPlatformData: true // 层级查询通常查询平台数据
  }
}

/**
 * 构建平台数据查询参数
 */
export const buildPlatformDataParams = (isPlatformData: boolean = true): Partial<UniversalSearchRequest> => {
  return {
    isPlatformData,
    enabled: 1 // 平台数据默认只查询启用的
  }
}


/**
 * 批量关键词搜索
 * 针对单个数据类型，传入多个关键词进行批量搜索
 * 返回 Map<关键词, List<UniversalSearchVO>>
 */
export const searchKeywords = (params: UniversalKeywordsSearchRequest) => {
  console.log('🔍 [API调用] searchKeywords 参数:', params)
  return request.post<UniversalKeywordsSearchResponse>(
    'mdm/universal-search/search-keywords',
    params,
    { showErrorMessage: false } as RequestConfigWithErrorMessage
  )
}
